// frontend/src/components/Dashboard/PayoutComponent.jsx
import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWallet,
  faDollarSign,
  faSpinner,
  faPlus,
  faTimes,
  faCheckCircle,
  faExclamationTriangle,
  faBank,
  faMobileAlt,
  faBuilding,
  faPhone,
  faHistory,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import { getPayoutHistory, requestPayout, getPayoutStats } from "../../api";
import { useCurrency } from "../../context/CurrencyContext";

const PayoutComponent = () => {
  const { formatPrice } = useCurrency();
  const [payouts, setPayouts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Form state
  const [formData, setFormData] = useState({
    amount: "",
    method: "bank_transfer",
    accountDetails: "",
  });

  // Sierra Leone payout methods
  const payoutMethods = [
    { value: "bank_transfer", label: "Bank Transfer", icon: faBank },
    { value: "mobile_money", label: "Mobile Money", icon: faMobileAlt },
    { value: "orange_money", label: "Orange Money", icon: faMobileAlt },
    { value: "afrimoney", label: "Afrimoney", icon: faMobileAlt },
  ];

  // Fetch payout data
  const fetchPayoutData = async () => {
    setLoading(true);
    try {
      const [historyRes, statsRes] = await Promise.all([
        getPayoutHistory(),
        getPayoutStats(),
      ]);
      setPayouts(historyRes.data?.data || []);
      setStats(statsRes.data || {});
    } catch (error) {
      console.error("Error fetching payout data:", error);
      setError("Failed to load payout data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayoutData();
  }, []);

  // Handle form change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");

    const amount = parseFloat(formData.amount);
    if (!amount || amount <= 0) {
      setError("Please enter a valid amount");
      setSubmitting(false);
      return;
    }

    if (!formData.method || !formData.accountDetails) {
      setError("Please fill in all fields");
      setSubmitting(false);
      return;
    }

    try {
      await requestPayout({
        amount: amount,
        method: formData.method,
        accountDetails: formData.accountDetails,
      });

      setSuccess("Payout requested successfully!");
      setFormData({
        amount: "",
        method: "bank_transfer",
        accountDetails: "",
      });

      await fetchPayoutData();
      setTimeout(() => setShowModal(false), 1500);
    } catch (error) {
      console.error("Error requesting payout:", error);
      setError(error.response?.data?.message || "Failed to request payout");
    } finally {
      setSubmitting(false);
    }
  };

  // Get status label
  const getStatusLabel = (status) => {
    const labels = {
      pending: "⏳ Pending",
      processing: "⚙️ Processing",
      completed: "✅ Completed",
      failed: "❌ Failed",
    };
    return labels[status] || status;
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      pending: "#f59e0b",
      processing: "#3b82f6",
      completed: "#16a34a",
      failed: "#dc2626",
    };
    return colors[status] || "#5A5A5A";
  };

  // Get method label
  const getMethodLabel = (method) => {
    const found = payoutMethods.find((m) => m.value === method);
    return found?.label || method.replace("_", " ").toUpperCase();
  };

  if (loading) {
    return (
      <div className="payout-loading">
        <FontAwesomeIcon icon={faSpinner} spin />
        <span>Loading payout data...</span>
      </div>
    );
  }

  return (
    <div className="payout-container">
      {/* Header */}
      <div className="payout-header">
        <h3>
          <FontAwesomeIcon icon={faWallet} /> Payout History
        </h3>
        <button className="btn-primary" onClick={() => setShowModal(true)}>
          <FontAwesomeIcon icon={faPlus} /> Request Payout
        </button>
      </div>

      {/* Error/Success */}
      {error && (
        <div className="payout-alert error">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>{error}</span>
          <button onClick={() => setError("")}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}
      {success && (
        <div className="payout-alert success">
          <FontAwesomeIcon icon={faCheckCircle} />
          <span>{success}</span>
          <button onClick={() => setSuccess("")}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="payout-stats-grid">
        <div className="payout-stat-card">
          <div className="payout-stat-icon">
            <FontAwesomeIcon icon={faDollarSign} />
          </div>
          <div className="payout-stat-info">
            <div className="payout-stat-value">
              {formatPrice(stats.totalEarnings || 0)}
            </div>
            <div className="payout-stat-label">Total Earnings</div>
          </div>
        </div>
        <div className="payout-stat-card">
          <div className="payout-stat-icon" style={{ color: "#f59e0b" }}>
            <FontAwesomeIcon icon={faClock} />
          </div>
          <div className="payout-stat-info">
            <div className="payout-stat-value">{stats.pendingPayouts || 0}</div>
            <div className="payout-stat-label">Pending Payouts</div>
          </div>
        </div>
        <div className="payout-stat-card">
          <div className="payout-stat-icon" style={{ color: "#16a34a" }}>
            <FontAwesomeIcon icon={faCheckCircle} />
          </div>
          <div className="payout-stat-info">
            <div className="payout-stat-value">
              {formatPrice(stats.totalPaid || 0)}
            </div>
            <div className="payout-stat-label">Total Paid</div>
          </div>
        </div>
      </div>

      {/* Payout History Table */}
      <div className="payout-history">
        <h4>
          <FontAwesomeIcon icon={faHistory} /> History
        </h4>
        <div className="table-responsive">
          <table className="payout-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Amount</th>
                <th>Method</th>
                <th>Account</th>
                <th>Status</th>
                <th>Transaction ID</th>
              </tr>
            </thead>
            <tbody>
              {payouts.length === 0 ? (
                <tr>
                  <td colSpan="6" className="no-data">
                    <FontAwesomeIcon icon={faWallet} />
                    <p>No payout history yet.</p>
                    <span>Request a payout to get started.</span>
                  </td>
                </tr>
              ) : (
                payouts.map((payout) => (
                  <tr key={payout._id}>
                    <td>{new Date(payout.createdAt).toLocaleDateString()}</td>
                    <td className="amount">{formatPrice(payout.amount)}</td>
                    <td>{getMethodLabel(payout.method)}</td>
                    <td className="account-details">{payout.accountDetails}</td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ background: getStatusColor(payout.status) }}
                      >
                        {getStatusLabel(payout.status)}
                      </span>
                    </td>
                    <td>{payout.transactionId || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Request Payout Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <FontAwesomeIcon icon={faWallet} /> Request Payout
              </h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="payout-form">
              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faDollarSign} /> Amount *
                </label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="Enter amount"
                  min="1"
                  step="0.01"
                  required
                />
              </div>

              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faBank} /> Payout Method *
                </label>
                <select
                  name="method"
                  value={formData.method}
                  onChange={handleChange}
                  required
                >
                  {payoutMethods.map((method) => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faBuilding} /> Account Details *
                </label>
                <input
                  type="text"
                  name="accountDetails"
                  value={formData.accountDetails}
                  onChange={handleChange}
                  placeholder={
                    formData.method === "bank_transfer"
                      ? "Bank: SLB, Account: 1234567890"
                      : "Phone: +232 76 123456"
                  }
                  required
                />
              </div>

              {error && (
                <div className="form-error">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <span>{error}</span>
                </div>
              )}

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin /> Processing...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheckCircle} /> Request Payout
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .payout-container {
          padding: 0.5rem 0;
        }

        .payout-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
          gap: 0.75rem;
        }

        .payout-header h3 {
          color: #006B2E;
          font-size: 1.1rem;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .payout-header h3 svg {
          color: #D9A321;
        }

        .payout-loading {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 2rem;
          justify-content: center;
          color: #5A5A5A;
        }

        .payout-loading svg {
          color: #D9A321;
        }

        .payout-alert {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          margin-bottom: 1rem;
          position: relative;
        }

        .payout-alert.error {
          background: #fee2e2;
          color: #dc2626;
          border-left: 4px solid #dc2626;
        }

        .payout-alert.success {
          background: #dcfce7;
          color: #16a34a;
          border-left: 4px solid #16a34a;
        }

        .payout-alert button {
          background: none;
          border: none;
          cursor: pointer;
          color: inherit;
          margin-left: auto;
          opacity: 0.7;
          padding: 0.25rem;
        }

        .payout-alert button:hover {
          opacity: 1;
        }

        .payout-stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .payout-stat-card {
          background: #ffffff;
          padding: 1rem;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          border: 1px solid #e5e5e5;
          transition: all 0.3s ease;
        }

        .payout-stat-card:hover {
          border-color: #D9A321;
          box-shadow: 0 4px 12px rgba(0,0,0,0.06);
        }

        .payout-stat-icon {
          width: 44px;
          height: 44px;
          min-width: 44px;
          background: #e8f5e9;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #006B2E;
          font-size: 1.1rem;
        }

        .payout-stat-info {
          flex: 1;
        }

        .payout-stat-value {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1F1F1F;
          font-family: "Montserrat", sans-serif;
        }

        .payout-stat-label {
          font-size: 0.65rem;
          color: #5A5A5A;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        .payout-history {
          background: #ffffff;
          border-radius: 12px;
          border: 1px solid #e5e5e5;
          overflow: hidden;
        }

        .payout-history h4 {
          padding: 1rem 1.25rem;
          margin: 0;
          color: #006B2E;
          font-size: 0.938rem;
          border-bottom: 1px solid #e5e5e5;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .payout-history h4 svg {
          color: #D9A321;
        }

        .table-responsive {
          overflow-x: auto;
        }

        .payout-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.813rem;
        }

        .payout-table th {
          padding: 0.75rem 1rem;
          text-align: left;
          font-weight: 600;
          color: #5A5A5A;
          background: #F8F8F8;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 1px solid #e5e5e5;
        }

        .payout-table td {
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #f0f0f0;
          color: #1F1F1F;
          vertical-align: middle;
        }

        .payout-table tr:hover td {
          background: rgba(0,107,46,0.02);
        }

        .payout-table .amount {
          font-weight: 600;
          color: #D9A321;
        }

        .payout-table .account-details {
          max-width: 150px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .payout-table .status-badge {
          display: inline-block;
          padding: 0.15rem 0.6rem;
          border-radius: 20px;
          font-size: 0.65rem;
          font-weight: 600;
          color: #ffffff;
        }

        .payout-table .no-data {
          text-align: center;
          padding: 2.5rem 1.5rem;
          color: #5A5A5A;
        }

        .payout-table .no-data svg {
          font-size: 2rem;
          color: #D9A321;
          display: block;
          margin: 0 auto 0.5rem;
        }

        .payout-table .no-data p {
          margin: 0.25rem 0;
        }

        .payout-table .no-data span {
          font-size: 0.75rem;
          color: #9ca3af;
        }

        /* Modal */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
        }

        .modal-content {
          background: #ffffff;
          border-radius: 16px;
          max-width: 480px;
          width: 100%;
          max-height: 95vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0,0,0,0.2);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.25rem 1.5rem;
          border-bottom: 1px solid #e5e5e5;
        }

        .modal-header h2 {
          color: #006B2E;
          font-size: 1.1rem;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .modal-header h2 svg {
          color: #D9A321;
        }

        .close-btn {
          background: none;
          border: none;
          font-size: 1.25rem;
          cursor: pointer;
          color: #5A5A5A;
          padding: 0.25rem;
          border-radius: 50%;
          transition: all 0.3s ease;
        }

        .close-btn:hover {
          background: #f0f0f0;
        }

        .payout-form {
          padding: 1.5rem;
        }

        .form-group {
          margin-bottom: 1rem;
        }

        .form-group label {
          display: block;
          font-weight: 500;
          font-size: 0.813rem;
          color: #1F1F1F;
          margin-bottom: 0.25rem;
        }

        .form-group label svg {
          color: #D9A321;
          margin-right: 0.25rem;
        }

        .form-group input,
        .form-group select {
          width: 100%;
          padding: 0.5rem 0.75rem;
          border: 1px solid #e5e5e5;
          border-radius: 8px;
          font-size: 0.875rem;
          transition: all 0.3s ease;
          background: #ffffff;
          color: #1F1F1F;
        }

        .form-group input:focus,
        .form-group select:focus {
          outline: none;
          border-color: #D9A321;
          box-shadow: 0 0 0 3px rgba(217,163,33,0.15);
        }

        .form-error {
          background: #fee2e2;
          color: #dc2626;
          padding: 0.5rem 0.75rem;
          border-radius: 8px;
          font-size: 0.813rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.75rem;
        }

        .form-actions {
          display: flex;
          gap: 0.75rem;
          justify-content: flex-end;
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid #e5e5e5;
        }

        .form-actions button {
          padding: 0.5rem 1.25rem;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          border: none;
        }

        .form-actions .btn-secondary {
          background: #f0f0f0;
          color: #1F1F1F;
        }

        .form-actions .btn-secondary:hover {
          background: #e5e5e5;
        }

        .form-actions .btn-primary {
          background: #D9A321;
          color: #ffffff;
        }

        .form-actions .btn-primary:hover:not(:disabled) {
          background: #B8860B;
        }

        .form-actions .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        @media (max-width: 480px) {
          .payout-stats-grid {
            grid-template-columns: 1fr;
          }
          
          .modal-content {
            max-width: 100%;
            margin: 0.5rem;
          }

          .form-actions {
            flex-direction: column;
          }

          .form-actions button {
            width: 100%;
          }

          .payout-table {
            font-size: 0.7rem;
          }

          .payout-table th,
          .payout-table td {
            padding: 0.4rem 0.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default PayoutComponent;
